import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ChevronLeft, Ellipsis, RefreshCw } from 'lucide-react';
import type { ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';

import {
  ActionRunnerProvider,
  useActionRunner,
  useHasActionRunner,
  type ActionSpec,
} from './actions';
import { resolvePatternText, type PatternText } from './text';

export interface PageHeaderProps {
  readonly title: ReactNode;
  /** Seção do menu, pequena acima do título (web). */
  readonly eyebrow?: ReactNode;
  /** Linha de contexto: contagem, "atualizado às…", o que a tela faz. */
  readonly description?: ReactNode;
  /** Voltar para a tela-mãe (formulário, detalhe). */
  readonly back?: { readonly label: string; readonly onClick: () => void };
  /** Ações do manifesto, na ordem; a `primary` fica por último na web e presa embaixo no iPhone. */
  readonly actions?: readonly ActionSpec[];
  /** Máximo de botões visíveis; o resto vai para "Mais ações" (U39 §6, legado 3–4). */
  readonly maxVisible?: number;
  readonly refreshAction?: { readonly onRefresh: () => unknown; readonly isRefreshing?: boolean };
  /** Conteúdo extra à direita (status, etiqueta). */
  readonly aside?: ReactNode;
  readonly className?: string;
}

/**
 * Cabeçalho de página (P03): um `h1` por tela, eyebrow, descrição e as ações do manifesto com o
 * contrato visual por `kind` (U39 §6). No iPhone, a ação primária fica presa embaixo (U12).
 */
export function PageHeader(props: PageHeaderProps) {
  const hasRunner = useHasActionRunner();
  if (hasRunner) return <PageHeaderContent {...props} />;
  return (
    <ActionRunnerProvider>
      <PageHeaderContent {...props} />
    </ActionRunnerProvider>
  );
}

function reasonOf(disabled: ActionSpec['disabled']): PatternText | undefined {
  return typeof disabled === 'string' || typeof disabled === 'object' ? disabled : undefined;
}

function ActionButton({
  spec,
  mobile,
  iconOnly = false,
}: {
  readonly spec: ActionSpec;
  readonly mobile: boolean;
  readonly iconOnly?: boolean;
}) {
  const { i18n } = useLingui();
  const runner = useActionRunner();
  const label = resolvePatternText(i18n, spec.label ?? spec.action.label);
  const reason = reasonOf(spec.disabled);
  const disabled = spec.disabled !== undefined && spec.disabled !== false;
  const Icon = spec.icon;
  const variant = spec.primary
    ? 'primary'
    : spec.action.kind === 'destructive'
      ? 'danger'
      : iconOnly
        ? 'ghost'
        : 'secondary';
  const button = (
    <Button
      variant={variant}
      size={iconOnly ? 'icon' : mobile && spec.primary ? 'lg' : 'default'}
      aria-disabled={disabled || undefined}
      {...(iconOnly ? { 'aria-label': label } : {})}
      onClick={() => {
        if (!disabled) void runner.run(spec);
      }}
      className={cn(mobile && spec.primary && 'shadow-3')}
    >
      {Icon ? <Icon aria-hidden /> : null}
      {iconOnly ? null : label}
    </Button>
  );
  if (!reason) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent>{resolvePatternText(i18n, reason)}</TooltipContent>
    </Tooltip>
  );
}

function Overflow({ actions }: { readonly actions: readonly ActionSpec[] }) {
  const { i18n, t } = useLingui();
  const runner = useActionRunner();
  if (actions.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t`Mais ações`}>
          <Ellipsis aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {actions.map((spec) => {
          const Icon = spec.icon;
          const disabled = spec.disabled !== undefined && spec.disabled !== false;
          return (
            <DropdownMenuItem
              key={spec.action.id}
              tone={spec.action.kind === 'destructive' ? 'danger' : 'default'}
              disabled={disabled}
              onSelect={() => void runner.run(spec)}
            >
              {Icon ? <Icon aria-hidden /> : null}
              {resolvePatternText(i18n, spec.label ?? spec.action.label)}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PageHeaderContent({
  title,
  eyebrow,
  description,
  back,
  actions = [],
  maxVisible = 3,
  aside,
  className,
  refreshAction,
}: PageHeaderProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const refresh = refreshAction ? (
    <Button
      variant="ghost"
      size="icon"
      aria-label={t`Atualizar`}
      loading={refreshAction.isRefreshing === true}
      onClick={() => {
        void refreshAction.onRefresh();
      }}
    >
      <RefreshCw aria-hidden />
    </Button>
  ) : null;
  const runner = useActionRunner();
  const available = actions.filter((spec) => spec.hidden !== true && runner.allowed(spec));
  const primary = available.find((spec) => spec.primary);
  const others = available.filter((spec) => spec !== primary);

  if (isMobile) {
    // iPhone: ações secundárias com ícone viram botões de ícone; o resto vai para o menu.
    const iconable = others
      .filter((spec) => spec.icon && spec.action.kind !== 'destructive')
      .slice(0, 2);
    const menu = others.filter((spec) => !iconable.includes(spec));
    return (
      <>
        <header data-slot="page-header" className={cn('flex flex-col gap-3', className)}>
          <div className="flex min-h-11 items-center gap-1">
            {back ? (
              <Button variant="link" className="-ml-1 gap-1 text-base" onClick={back.onClick}>
                <ChevronLeft aria-hidden className="size-5" />
                {back.label}
              </Button>
            ) : null}
            <div className="ml-auto flex items-center gap-1">
              {refresh}
              {iconable.map((spec) => (
                <ActionButton key={spec.action.id} spec={spec} mobile iconOnly />
              ))}
              <Overflow actions={menu} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <h1 className="text-3xl leading-tight font-semibold tracking-tight text-foreground">
              {title}
            </h1>
            {description ? (
              <div className="text-sm text-muted-foreground">{description}</div>
            ) : null}
            {aside}
          </div>
        </header>
        {primary ? (
          <div className="pointer-events-none sticky bottom-[calc(env(safe-area-inset-bottom,0px)+16px)] z-20 order-last flex justify-end">
            <div className="pointer-events-auto">
              <ActionButton spec={primary} mobile />
            </div>
          </div>
        ) : null}
      </>
    );
  }

  const visible = others.slice(0, Math.max(0, maxVisible - (primary ? 1 : 0)));
  const overflow = others.slice(visible.length);
  return (
    <header data-slot="page-header" className={cn('flex flex-col gap-4', className)}>
      {back ? (
        <Button variant="link" className="-ml-0.5 w-fit gap-1.5" onClick={back.onClick}>
          <ArrowLeft aria-hidden className="size-4" />
          {back.label}
        </Button>
      ) : null}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {eyebrow ? (
            <p className="text-xs font-semibold tracking-[0.12em] text-accent-text uppercase">
              {eyebrow}
            </p>
          ) : null}
          <h1 className="text-3xl leading-tight font-semibold tracking-tight text-foreground">
            {title}
          </h1>
          {description ? <div className="text-sm text-muted-foreground">{description}</div> : null}
        </div>
        {available.length > 0 || refresh ? (
          <div className="flex flex-wrap items-center gap-2">
            {refresh}
            <Overflow actions={overflow} />
            {visible.map((spec) => (
              <ActionButton key={spec.action.id} spec={spec} mobile={false} />
            ))}
            {primary ? <ActionButton spec={primary} mobile={false} /> : null}
          </div>
        ) : null}
        {aside}
      </div>
    </header>
  );
}
